import { useState, useEffect, useCallback } from 'react';
import { notify } from '../../../utils/toast';
import {
    saveMetaIntegration,
    getMetaIntegration,
    removeMetaIntegration,
    clearMetaOAuthSession
} from '../../../services/metaService';
import { useMetaAuthFlow } from './useMetaAuthFlow';
import { 
    MetaConnectionState, 
    UseMetaConnectionReturn, 
    MetaSelectionData 
} from '../types/meta';

/**
 * Hook para gerenciar conexão Meta Ads
 * 
 * @param organizationId - ID da organização atual
 */
export function useMetaConnection(organizationId: string | undefined): UseMetaConnectionReturn {
    // Hook de Auth Flow (Separado para reduzir complexidade)
    const { 
        isConnecting, 
        authModalData, 
        startAuthFlow, 
        checkPendingAuth,
        clearAuthData 
    } = useMetaAuthFlow();

    // Estado principal da conexão
    const [state, setState] = useState<MetaConnectionState>({
        connecting: false,
        connected: false
    });

    const [isSaving, setIsSaving] = useState(false);

    // Modal aberto <=> há dados de auth (evita race isModalOpen atrasado 1 frame)
    const isModalOpen = authModalData !== null;

    /**
     * Carrega status da integração ao montar/mudar organização
     */
    useEffect(() => {
        if (organizationId) {
            loadStatus();
            checkPendingAuth();
        }
    }, [organizationId]);

    /**
     * Carrega status atual da integração
     */
    const loadStatus = useCallback(async () => {
        if (!organizationId) return;

        try {
            const integration = await getMetaIntegration(organizationId);

            if (integration) {
                setState({
                    connecting: false,
                    connected: true,
                    accountName: integration.accountName,
                    pixelName: integration.pixelName,
                    pageName: integration.pageName,
                    expiresAt: integration.expiresAt
                });
            } else {
                setState({
                    connecting: false,
                    connected: false
                });
            }
        } catch (error) {
            console.error('[useMetaConnection] Erro ao carregar status:', error);
        }
    }, [organizationId]);

    const connect = useCallback(async () => {
        if (!organizationId) {
            notify.error('Organização não carregada. Aguarde um instante e tente de novo.');
            return;
        }
        await startAuthFlow(organizationId);
    }, [organizationId, startAuthFlow]);

    const save = useCallback(async (selection: MetaSelectionData) => {
        const targetOrgId = authModalData?.organizationId ?? organizationId;
        if (!targetOrgId || !authModalData) return;

        setIsSaving(true);

        try {
            await saveMetaIntegration({
                account: selection.account,
                pixel: selection.pixel,
                page: selection.page,
                accessToken: authModalData.accessToken
            }, targetOrgId);

            setState({
                connecting: false,
                connected: true,
                accountName: selection.account.name,
                pixelName: selection.pixel?.name,
                pageName: selection.page?.name
            });

            closeModal();
            notify.success('Meta Ads conectado. O rastreamento já está ativo.');
        } catch (error: any) {
            console.error('[useMetaConnection] Erro ao salvar:', error);
            notify.error(error.message || 'Não foi possível salvar a conexão Meta.');
        } finally {
            setIsSaving(false);
        }
    }, [organizationId, authModalData]);

    const disconnect = useCallback(async () => {
        if (!organizationId) return;

        const confirmed = await notify.confirm(
            'Desconectar Meta Ads?',
            {
                description: 'O rastreamento de leads será interrompido até você conectar de novo.',
                confirmText: 'Desconectar',
                cancelText: 'Manter conectado'
            }
        );

        if (!confirmed) return;

        try {
            await removeMetaIntegration(organizationId);
            clearMetaOAuthSession();
            setState({
                connecting: false,
                connected: false,
                accountName: undefined,
                pixelName: undefined,
                pageName: undefined,
                expiresAt: undefined
            });
            clearAuthData();
            notify.success('Meta Ads desconectado.');
        } catch (error: any) {
            console.error('Erro ao desconectar:', error);
            notify.error(error.message || 'Não foi possível desconectar o Meta Ads.');
        }
    }, [organizationId, clearAuthData]);

    const reconnect = useCallback(async () => {
        if (!organizationId) return;

        try {
            await removeMetaIntegration(organizationId);
            clearMetaOAuthSession();
            setState({
                connecting: false,
                connected: false
            });
            await connect();
        } catch (error: any) {
            console.error('[useMetaConnection] Erro ao reconectar:', error);
            notify.error(error.message || 'Não foi possível reconectar no Meta.');
        }
    }, [organizationId, connect]);

    const closeModal = useCallback(() => {
        clearAuthData();
        clearMetaOAuthSession();
    }, [clearAuthData]);

    const formatExpiryDate = useCallback((dateString?: string): string | null => {
        if (!dateString) return null;

        const date = new Date(dateString);
        const now = new Date();
        const daysLeft = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysLeft < 0) return 'Expirado';
        if (daysLeft === 0) return 'Expira hoje';
        if (daysLeft === 1) return 'Expira amanhã';
        return `Expira em ${daysLeft} dias`;
    }, []);

    const isTokenExpired = useCallback((): boolean => {
        if (!state.expiresAt) return false;
        return new Date(state.expiresAt) < new Date();
    }, [state.expiresAt]);

    return {
        state: {
            ...state,
            connecting: isConnecting
        },
        modalData: authModalData,
        isModalOpen,
        isSaving,
        isTokenExpired: isTokenExpired(),
        connect,
        disconnect,
        reconnect,
        save,
        closeModal,
        formatExpiryDate
    };
}

export default useMetaConnection;
